export interface PuzzleShardMeta {
  file: string;
  count: number;
  minRating: number;
  maxRating: number;
}

export interface PuzzleThemeMeta {
  theme: string;
  count: number;
}

export interface PuzzleIndex {
  generatedAt: string;
  source: string;
  license: string;
  total: number;
  shards: PuzzleShardMeta[];
  themes: PuzzleThemeMeta[];
}

export interface Puzzle {
  i: string;
  f: string;
  m: string;
  r: number;
  t: string;
  p: number;
}

export const PUZZLE_BASE = `${import.meta.env.BASE_URL}puzzles`;

export async function loadPuzzleIndex(): Promise<PuzzleIndex> {
  const response = await fetch(`${PUZZLE_BASE}/index.json`);
  if (!response.ok) throw new Error("Could not load the puzzle index.");
  return (await response.json()) as PuzzleIndex;
}

const shardCache = new Map<string, Promise<Puzzle[]>>();

export function loadShard(file: string): Promise<Puzzle[]> {
  const cached = shardCache.get(file);
  if (cached) return cached;
  const request = fetch(`${PUZZLE_BASE}/${file}`)
    .then((response) => {
      if (!response.ok) throw new Error(`Could not load puzzle shard ${file}.`);
      return response.json() as Promise<Puzzle[]>;
    })
    .catch((error: unknown) => {
      shardCache.delete(file);
      throw error;
    });
  shardCache.set(file, request);
  return request;
}

export function shardForRating(index: PuzzleIndex, rating: number): PuzzleShardMeta {
  const target = Math.min(Math.max(rating, 500), 2500);
  const exact = index.shards.find(
    (shard) => target >= shard.minRating && target <= shard.maxRating,
  );
  if (exact) return exact;

  let best = index.shards[0];
  if (!best) throw new Error("The puzzle index has no shards.");
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const shard of index.shards) {
    const distance = target < shard.minRating
      ? shard.minRating - target
      : target - shard.maxRating;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = shard;
    }
  }
  return best;
}

export interface PuzzleFilters {
  count: number;
  rating: number;
  themes: string[];
  exclude: Set<string>;
}

export function selectPuzzles(puzzles: Puzzle[], filters: PuzzleFilters): Puzzle[] {
  const window = 260;
  const candidates = puzzles.filter((puzzle) => {
    if (filters.exclude.has(puzzle.i)) return false;
    if (Math.abs(puzzle.r - filters.rating) > window) return false;
    if (filters.themes.length === 0) return true;
    const tags = puzzle.t ? puzzle.t.split(" ") : [];
    return filters.themes.some((theme) => tags.includes(theme));
  });

  const pool = candidates.length >= filters.count ? candidates : puzzles.filter((p) => !filters.exclude.has(p.i));
  return shuffle(pool).slice(0, filters.count);
}

export function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    const current = copy[index];
    const swapped = copy[target];
    if (current === undefined || swapped === undefined) continue;
    copy[index] = swapped;
    copy[target] = current;
  }
  return copy;
}

export const PUZZLE_THEME_LABELS: Array<[string, string]> = [
  ["mateIn1", "Mate in 1"],
  ["mateIn2", "Mate in 2"],
  ["mateIn3", "Mate in 3"],
  ["fork", "Fork"],
  ["pin", "Pin"],
  ["skewer", "Skewer"],
  ["discoveredAttack", "Discovered attack"],
  ["defensiveMove", "Defence"],
  ["crushing", "Crushing"],
  ["advantage", "Advantage"],
  ["endgame", "Endgame"],
  ["sacrifice", "Sacrifice"],
  ["short", "Short"],
  ["long", "Long"],
];
